/**
 * The camera as the player draws it (SKEL-STORY §28). Pure: no pixi, no DOM, no clock.
 *
 * A bundle's `camera` cues are written at bake time by the director
 * (`backend/app/story/camera.py`); `runner.ts` replays them into `StageState.camera` with
 * `interpCamera`, and `world.ts` draws `viewOf(state.camera, reducedMotion)`:
 *
 * - **Conventions.** Stage percent, y down. A view `{cx, cy, zoom}` shows the rectangle
 *   `[cx ± 50/zoom] × [cy ± 50/zoom]` (the stage keeps its 16:9 aspect, so a square in percent).
 *   `zoom` 1 is the whole stage; the player never shows less than half of it (`CAMERA_ZOOM_MAX`)
 *   and never anything outside the plate (`clampView`).
 * - **Moves lerp the centre and the view width** (`1/zoom`), not the zoom: every edge of the frame
 *   then moves in a straight line, so a subject inside both ends of a move stays inside all the
 *   way, and a pull-back never shows the plate's edge on the way out.
 * - **Reduced motion cuts.** A move toward a still shot (wide, cast, single, two, close) is replaced
 *   by a cut to its frame when the move starts; a follow, action or end shot stays wide; the slow
 *   push-in leaves the framing alone (`cameraCut`, `viewOf`).
 * - **Screen points.** What the canvas shows at stage point `p` is at `toScreen(view, p)` in percent
 *   of the visible frame — speech bubbles and the HUD anchors live there, outside the camera.
 */

import type { CameraShot } from '@/schema/story'

export type { CameraShot }

/** A framing: the centre (stage %) and how far in (1 = the whole stage). */
export interface CameraFrame {
  target: [number, number]
  zoom: number
}

/** `StageState.camera`: the frame now, plus what reduced motion draws instead (§28). */
export interface CameraLike extends CameraFrame {
  /** Reduced motion's framing: set by each camera cue at its `t`; absent before the first one. */
  cut?: CameraFrame
  /** The shot of the last camera cue that started. */
  shot?: CameraShot | null
}

/** What `world.ts` draws: a clamped, drawable view. */
export interface CameraView {
  cx: number
  cy: number
  zoom: number
}

export const WIDE_VIEW: Readonly<CameraView> = Object.freeze({ cx: 50, cy: 50, zoom: 1 })
export const WIDE_FRAME: Readonly<CameraFrame> = Object.freeze({ target: [50, 50] as [number, number], zoom: 1 })

/** Never less than the whole stage (outside the plate is nothing), never more than half of it. */
export const CAMERA_ZOOM_MIN = 1
export const CAMERA_ZOOM_MAX = 2

/** Shots reduced motion turns into a cut to their own frame. */
export const STILL_SHOTS: ReadonlySet<CameraShot> = new Set<CameraShot>(['wide', 'cast', 'single', 'two', 'close'])

const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v)
const finite = (v: number, fallback: number): number => (Number.isFinite(v) ? v : fallback)

/** A drawable view of `target` at `zoom`: zoom in `[1, 2]`, the frame inside the plate. */
export function clampView(target: readonly [number, number], zoom: number): CameraView {
  const z = clamp(finite(zoom, 1), CAMERA_ZOOM_MIN, CAMERA_ZOOM_MAX)
  const half = 50 / z
  return {
    cx: clamp(finite(target[0], 50), half, 100 - half),
    cy: clamp(finite(target[1], 50), half, 100 - half),
    zoom: z,
  }
}

/** A move `k` of the way (already eased) from `from` to `to`: the centre and `1/zoom` lerp. */
export function interpCamera(from: CameraFrame, to: CameraFrame, k: number): CameraFrame {
  const z0 = from.zoom > 0 ? from.zoom : 1
  const z1 = to.zoom > 0 ? to.zoom : 1
  const w = 1 / z0 + (1 / z1 - 1 / z0) * k
  return {
    target: [from.target[0] + (to.target[0] - from.target[0]) * k, from.target[1] + (to.target[1] - from.target[1]) * k],
    zoom: w > 0 ? 1 / w : z1,
  }
}

/**
 * The frame reduced motion cuts to when a camera cue of `shot` starts (its own goal for a still
 * shot or a cue with no shot, wide for a moving one), or `null` to keep the cut there is (`push`).
 */
export function cameraCut(shot: CameraShot | null | undefined, goal: CameraFrame): CameraFrame | null {
  if (shot === 'push') return null
  if (!shot || STILL_SHOTS.has(shot)) return { target: [goal.target[0], goal.target[1]], zoom: goal.zoom }
  return { target: [50, 50], zoom: 1 }
}

/** The view `world.ts` draws for a snapshot's camera: the cut under reduced motion, else the frame. */
export function viewOf(cam: CameraLike, reducedMotion: boolean): CameraView {
  const f = reducedMotion ? (cam.cut ?? WIDE_FRAME) : cam
  return clampView(f.target, f.zoom)
}

/** Stage point `p` (stage %) → percent of the visible frame. */
export function toScreen(v: CameraView, p: readonly [number, number]): [number, number] {
  return [(p[0] - v.cx) * v.zoom + 50, (p[1] - v.cy) * v.zoom + 50]
}

/** The visible rectangle of `v`, stage %: `[x0, y0, x1, y1]`. */
export function viewRect(v: CameraView): [number, number, number, number] {
  const half = 50 / v.zoom
  return [v.cx - half, v.cy - half, v.cx + half, v.cy + half]
}

/** A stable string for "did the view change" checks (DOM writes only on change). */
export function viewKey(v: CameraView): string {
  return `${v.cx.toFixed(3)},${v.cy.toFixed(3)},${v.zoom.toFixed(4)}`
}
